import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronDown, Newspaper, User } from 'lucide-react';
import { navigation } from '../../mock';
import { useLang } from '../../i18n/LanguageContext';
import { api, resolveUrl } from '../../services/api';
import YppsdpLogo from '../YppsdpLogo';

const SearchBox = ({ compact = false }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef();
  const nav = useNavigate();
  const { t, tv } = useLang();

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults(null); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try { setResults(await api.search(q)); } catch { setResults({ news: [], leadership: [] }); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const goNews = (slug) => { setOpen(false); setQ(''); nav(`/berita/${slug}`); };
  const goLead = () => { setOpen(false); setQ(''); nav('/pimpinan'); };
  const total = (results?.news?.length || 0) + (results?.leadership?.length || 0);

  return (
    <div className={`relative ${compact ? 'w-full' : 'w-64'}`} ref={boxRef}>
      <div className="flex items-center bg-white/5 border border-white/20 rounded-md overflow-hidden focus-within:border-[#d4a017] transition">
        <input
          data-testid="search-input"
          type="text" value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={t('search')}
          className="bg-transparent px-4 py-2 text-sm placeholder-white/50 focus:outline-none flex-1"
        />
        <button className="px-3 py-2 bg-[#d4a017] hover:bg-[#b8860b] text-[#0a1929] transition"><Search size={16} /></button>
      </div>
      {open && q.length >= 2 && (
        <div data-testid="search-results" className="absolute left-0 right-0 mt-1 bg-white text-gray-900 shadow-2xl border-t-2 border-[#d4a017] max-h-[70vh] overflow-y-auto z-50">
          {loading && <div className="px-4 py-3 text-sm text-gray-500">Mencari...</div>}
          {!loading && total === 0 && <div className="px-4 py-4 text-sm text-gray-500">Tidak ada hasil untuk "{q}"</div>}
          {!loading && results?.news?.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] tracking-widest font-bold text-gray-500 bg-gray-50 border-b">BERITA · {results.news.length}</div>
              {results.news.map((n) => (
                <button key={n.id} onClick={() => goNews(n.slug || n.id)} className="w-full text-left flex items-center gap-3 px-4 py-2.5 border-b hover:bg-[#f5f5f5]">
                  <img src={resolveUrl(n.image)} alt="" className="w-12 h-9 object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0a1929] truncate">{tv(n.title)}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2"><Newspaper size={10} /> {n.category} · {n.date}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {!loading && results?.leadership?.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] tracking-widest font-bold text-gray-500 bg-gray-50 border-b">PENGURUS · {results.leadership.length}</div>
              {results.leadership.map((l) => (
                <button key={l.id} onClick={goLead} className="w-full text-left flex items-center gap-3 px-4 py-2.5 border-b hover:bg-[#f5f5f5]">
                  <img src={resolveUrl(l.image)} alt="" className="w-9 h-9 object-cover rounded-full flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0a1929] truncate">{l.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2"><User size={10} /> {tv(l.title)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();
  const { t, tv } = useLang();

  return (
    <header className="bg-[#0a1929] text-white sticky top-0 z-50 shadow-lg">
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <YppsdpLogo size={64} className="drop-shadow-lg group-hover:scale-105 transition" />
            <div className="leading-tight max-w-[380px]">
              <div className="text-[10px] tracking-[0.18em] text-[#d4a017] font-bold uppercase">Yayasan</div>
              <div className="text-2xl font-bold tracking-wide" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>PPSDP</div>
              <div className="text-[10px] tracking-wider text-white/70 leading-snug mt-0.5">Pengembangan Potensi Sumber Daya Pertahanan</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-4">
            <SearchBox />
            <div className="text-right text-[11px] text-white/60 leading-tight">
              <div>{t('headOffice')}</div>
              <div className="text-[#d4a017] font-semibold">{t('address')}</div>
            </div>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 hover:bg-white/10 rounded transition">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <nav className="hidden lg:block bg-[#0f2340] border-t border-[#d4a017]/40">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center">
            <li>
              <Link to="/" className={`block px-5 py-3.5 text-sm font-semibold tracking-wide uppercase border-r border-white/10 transition ${location.pathname === '/' ? 'bg-[#d4a017] text-[#0a1929]' : 'hover:bg-white/10'}`}>
                {t('home')}
              </Link>
            </li>
            {navigation.map((item) => (
              <li key={tv(item.label)} className="relative" onMouseEnter={() => setOpenMenu(tv(item.label))} onMouseLeave={() => setOpenMenu(null)}>
                <button className="flex items-center gap-1 px-5 py-3.5 text-sm font-semibold tracking-wide uppercase border-r border-white/10 hover:bg-white/10 transition">
                  {tv(item.label)}
                  <ChevronDown size={14} className={`transition ${openMenu === tv(item.label) ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === tv(item.label) && (
                  <div className="nav-dropdown absolute top-full left-0 min-w-[260px] bg-[#0a1929] border-t-2 border-[#d4a017] shadow-2xl">
                    {item.children.map((c) => (
                      <Link key={tv(c.label)} to={c.href} className="block px-5 py-3 text-sm border-b border-white/5 hover:bg-[#d4a017] hover:text-[#0a1929] transition">
                        {tv(c.label)}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0f2340] border-t border-white/10">
          <div className="px-4 py-3">
            <div className="mb-3"><SearchBox compact /></div>
            <Link to="/" onClick={() => setMobileOpen(false)} className="block px-3 py-3 text-sm font-semibold uppercase border-b border-white/10">{t('home')}</Link>
            {navigation.map((item) => (
              <div key={tv(item.label)} className="border-b border-white/10">
                <div className="px-3 py-3 text-sm font-semibold uppercase text-[#d4a017]">{tv(item.label)}</div>
                {item.children.map((c) => (
                  <Link key={tv(c.label)} to={c.href} onClick={() => setMobileOpen(false)} className="block px-6 py-2 text-sm text-white/80 hover:text-white">{tv(c.label)}</Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
